import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IStrategy } from '../../types/model';
import {
  computePercentage,
  formatDate,
  getOptionsList,
  getSymbol,
  getTotalValues,
} from '../../helpers/functions';
import { CustomNumber } from '../CustomNumber/CustomNumber';
import { ProfitLoss } from '../ProfitLoss';
import { TTickerCurrency } from '../../types/model';
import { ColorButton, TagList, ToggleButton } from '../UI';
import { Options } from './Options';
import { useStrategiesContext } from './StrategiesContext';
import { useNavigate } from 'react-router';

export const StrategyLineCard = ({ strategy }: { strategy: IStrategy }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    loadingStrategyActivationId,
    handleOnSelect,
    handleStrategyActivation,
  } = useStrategiesContext();

  const {
    active,
    archived,
    color,
    createdAt,
    id,
    currency,
    name,
    startDate,
    endDate,
    strategyAssets,
    tags,
  } = strategy;

  const optionsList = getOptionsList(archived);
  const {
    totalValue: lastTotalValue,
    variations: { VAR_ALL: startValue },
  } = getTotalValues(
    strategy?.strategyAssets?.items,
    currency.toLocaleLowerCase() as TTickerCurrency,
    strategy.createdAt
  );

  const percentage = computePercentage(startValue, lastTotalValue, 100);

  return (
    <div
      key={id}
      className={`
        flex
        flex-col
        sm:flex-row
        items-center
        border
        border-grey-30
        h-auto
        sm:h-20
        rounded-xl
        shadow-md
        mx-auto
        mt-5
        px-5
        dark:bg-grey-80
        dark:border-grey-70
        dark:text-white
        relative
      `}
    >
      <div
        className={`
        flex
        flex-col
        sm:flex-row
        items-center
        cursor-pointer
        xl:w-10/12
        lg:w-10/12
        md:w-10/12
        w-full
      `}
        onClick={() => navigate('/strategies/' + id)}
      >
        {/* 1 */}
        <div
          className={`
          flex
          w-full
          xl:w-4/12
          lg:w-4/12
          md:w-4/12
          sm:w-1/2
          items-center
          py-4
          pr-4
        `}
        >
          {/* strategy color & name */}
          <div className="flex items-center w-3/4 justify-start pr-4 h-16">
            <ColorButton isReadOnly={true} radius={3} color={color} />
            <h3
              className={`
              pl-3
              w-full
              text-md
              font-semibold
              truncate
              flex-grow-0
            `}
            >
              {name}
            </h3>
          </div>

          {/* ACTIVE & toggle */}
          <div className="flex relative w-1/4 font-semibold text-sm">
            <div className="mr-2">{t('strategies.active')}</div>
            <div className={'absolute left-12'}>
              <ToggleButton
                onActivate={() => handleStrategyActivation(id, !active)}
                onDeactivate={() => handleStrategyActivation(id, !active)}
                isActivated={active}
                isLoading={loadingStrategyActivationId === id}
                isDisabled={!!loadingStrategyActivationId}
              />
            </div>
          </div>
        </div>

        {/* 2 */}
        <div
          className={`
          flex
          w-full
          xl:w-5/12
          lg:w-5/12
          md:w-5/12
          sm:w-1/2
          overflow-hidden
          py-4
          pr-4
          ml-8
        `}
        >
          {/* total value */}
          <div className="w-1/2 pr-4">
            <div className="text-grey-50 text-xs">
              {t('strategies.totalValue')}
            </div>
            <CustomNumber
              className="font-semibold text-sm"
              suffix={getSymbol(currency)}
              value={lastTotalValue}
              displayType={'text'}
            />
          </div>

          {/* Profit/loss */}
          <div className="w-1/2">
            <div className="text-grey-50 text-xs">
              {t('strategies.totalProfit')}
            </div>
            <ProfitLoss
              isRow
              symbol={getSymbol(currency)}
              percentage={percentage}
              percentageClassName="font-semibold"
              value={lastTotalValue - startValue}
              valueClassName="w-28 font-semibold"
              displayType={'text'}
            />
          </div>
        </div>

        {/* 3 */}
        <div className="hidden md:flex w-3/12 overflow-hidden pr-4">
          {/* asset */}
          <div className="w-1/4 mx-4 flex-grow-0 ">
            <div className="text-grey-50 text-xs">{t('strategies.asset')}</div>
            <div className={'text-sm'}>{strategyAssets.items.length}</div>
          </div>

          {/* date */}
          <div className="w-3/4 flex-grow-0 ">
            <div className="text-grey-50 text-xs">{t('strategies.date')}</div>
            <div className="text-sm truncate">
              {startDate && endDate
                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                : formatDate(createdAt)}
            </div>
          </div>
        </div>
      </div>

      {/* 4 */}
      <div
        className={`
          sm:w-2/12
          md:w-2/12
          lg:w-2/12
          items-center
          w-full
          h-full
          justify-center
          overflow-hidden
          cursor-pointer
          mr-8
        `}
        onClick={() => navigate('/strategies/' + id)}
      >
        {/* tags */}
        <div
          className="flex h-full
          justify-end mr-auto overflow-hidden items-center"
        >
          <div>
            <TagList tagList={strategy.tags} maxTags={2} isReadOnly={true} />
          </div>
        </div>
      </div>
      {/* options */}
      <Options
        className="cursor-pointer z-40 absolute right-4 justify-center items-center"
        onSelect={(option) => handleOnSelect(option, strategy)}
        options={optionsList}
      />
    </div>
  );
};
